import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, setToken, errorText } from "@/lib/api";
import { Field, inputCls, PrimaryButton, errorCls } from "@/components/agent/ui";
import { LogoMark } from "@/components/agent/AgentLayout";

const Shell = ({ children }) => (
  <div className="relative isolate flex min-h-screen flex-col overflow-x-clip bg-background">
    <header className="flex h-16 w-full items-center justify-between px-4 md:px-6">
      <Link to="/welcome" className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground hover:text-foreground" data-testid="signup-back">
        <ArrowLeft className="size-4" /> Back
      </Link>
      <Link to="/login" className="rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="signup-login-link">Sign in</Link>
    </header>
    <main className="flex flex-1 items-center justify-center px-6 pb-16"><div className="w-full max-w-sm">{children}</div></main>
  </div>
);

const Title = ({ title, sub }) => (
  <div className="flex flex-col items-center gap-3 text-center">
    <LogoMark className="size-10 rounded-xl text-lg" />
    <div>
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{sub}</p>
    </div>
  </div>
);

const DetailsStep = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", agency_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setToken((await api.post("/auth/signup", form)).data.token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-testid="signup-form">
      <Title title="Create your Landin account" sub="Add properties and start referencing tenants today." />
      <div className="flex flex-col gap-4">
        <Field label="Your name"><input required autoComplete="name" value={form.name} onChange={set("name")} className={inputCls} data-testid="signup-name-input" /></Field>
        <Field label="Agency name"><input required autoComplete="organization" value={form.agency_name} onChange={set("agency_name")} className={inputCls} data-testid="signup-agency-input" /></Field>
        <Field label="Work email"><input required type="email" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="signup-email-input" /></Field>
        <Field label="Password" help="At least 8 characters.">
          <input required type="password" minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")} className={inputCls} data-testid="signup-password-input" />
        </Field>
      </div>
      {error && <p className={`${errorCls} text-center`} data-testid="signup-error">{error}</p>}
      <PrimaryButton type="submit" size="lg" isLoading={busy} testid="signup-submit-button">Create account</PrimaryButton>
      <p className="text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-medium text-foreground underline underline-offset-4">Sign in</Link></p>
    </form>
  );
};

/** Classic sign-up. The account opens straight into an empty workspace. */
export default function Signup() {
  return <Shell><DetailsStep /></Shell>;
}
